import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { Fade, Grid, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getDataFull, deleteData, postData } from '../../services/ajax/';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import MaterialTable, { DataType } from '../../components/material-table';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useSnackbar } from 'notistack';
import VerLogDialog from '../../components/modal/log/verLog';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import { blue, green, orange, red } from '@mui/material/colors';
//import styles from './style.module.scss';

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',

        width: '100%',
        marginBottom: theme.spacing(2),
    },
    fixedHeight: {
        height: 240,
    },
}));

function Page() {
    const { classes } = useStyles();
    //Auxiliar data
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);

    //Modals
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar/*, closeSnackbar*/ } = useSnackbar();

    const [referenciaVerLogModal, setReferenciaVerLogModal] = useState(null);
    const [idReferenciaVerLogModal] = useState(null);

    const onNuevo = useCallback(async (row) => {
        globalsContext.setIdEdit(false);
        navigate("/usuario/nuevo");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [/*globalsContext, */navigate]);

    const onEditar = useCallback(async (row) => {
        globalsContext.setIdEdit(row.id_usuario);
        navigate("/usuario/editar");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario.id_usuario]);

    const onEliminar = useCallback(async (row) => {
        alertModal.showModal(
            "Eliminar usuario",
            "El usuario " + (row.nombre_apellido || row.nombre) + " será eliminado. ¿Deseas continuar?",
            async function () {
                loadingModal.setOpen(true);

                const response = await deleteData(`usuarios/${row.id_usuario}`, usuario.token);

                if (response.error === "") {
                    enqueueSnackbar("Usuario eliminado ok", { variant: "success" });
                    setTableMainNeedUpdate(Date.now());
                }
                else
                    enqueueSnackbar(response.error, { variant: "error" });

                loadingModal.setOpen(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario.token]);

    const onReestablecerClave = useCallback(async (row) => {
        alertModal.showModal(
            "Reestablecer contraseña",
            "Se enviará un email a " + (row.email || row.nombre_apellido || row.nombre) + " con un enlace para reestablecer su contraseña. ¿Deseas continuar?",
            async function () {
                loadingModal.setOpen(true);

                // Endpoint autenticado: localiza al usuario por id (no depende de CUIL/email coincidentes como /usuarios/recover)
                const response = await postData(`usuarios/${row.id_usuario}/reset-password`, {}, usuario.token);

                if (response.error === "") {
                    enqueueSnackbar("Se ha enviado un email con el enlace para reestablecer la contraseña", { variant: "success" });
                }
                else
                    enqueueSnackbar(response.error, { variant: "error" });

                loadingModal.setOpen(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario.token]);

    //MaterialTable elements
    const [rowsTableMain, setRowsTableMain] = useState(null);
    let [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());
    const tableMain = useMemo(() => ({
        title: "Usuarios",
        idName: "id_usuario",
        defaultOrder: "asc",
        headers: [
            { id: 'cuil', label: 'CUIT/CUIL', dataType: DataType.CUIL, sortable: true },
            { id: 'nombre_apellido', label: 'Nombre', dataType: DataType.TEXT, sortable: true },
            { id: 'email', label: 'Email', dataType: DataType.TEXT, sortable: true },
            { id: 'nivel_usuario', label: 'Nivel', dataType: DataType.TEXT, sortable: true },
            { id: 'sup_usuarios', label: 'Sup. Usuarios', dataType: DataType.TEXT, sortable: true },
        ],
        loadData: async (orderBy, order, page, rowsPerPage, searchText, selectedFilter, pageUpdate, totalUpdate, filterListUpdate) => {
            setRowsTableMain(null);

            // Construir query params para la API REST
            // La API usa paginación basada en 1, MaterialTable usa basada en 0
            const apiPage = page + 1;
            const params = new URLSearchParams();
            params.append('per_page', rowsPerPage.toString());
            params.append('page', apiPage.toString());
            if (orderBy) params.append('order_by', orderBy);
            if (order) params.append('order', order);
            if (searchText) params.append('search_text', searchText);
            if (selectedFilter) params.append('selected_filter', selectedFilter);

            const response = await getDataFull(`usuarios?${params.toString()}`, usuario.token);
            
            if (response.error === "") {
                // Mapear los datos de la API al formato esperado por la tabla
                const mappedData = (response.data || []).map(usuario => ({
                    ...usuario,
                    // Mapear campos para compatibilidad
                    nombre: usuario.nombre_apellido,
                    cuitCuil: usuario.cuil,
                    // Convertir booleanos a texto para mostrar
                    nivel_usuario: usuario.nivel_usuario === 1 ? 'Administrador' : 
                                   usuario.nivel_usuario === 2 ? 'Supervisor' : 'Usuario',
                    sup_usuarios: usuario.sup_usuarios ? 'Sí' : 'No',
                }));

                // Convertir de paginación basada en 1 (API) a basada en 0 (MaterialTable)
                const tablePage = response.meta?.page ? response.meta.page - 1 : page;
                pageUpdate(tablePage);
                totalUpdate(response.meta?.total || 0);
                filterListUpdate([]); // La API no devuelve filterList por ahora
                setRowsTableMain(mappedData);
            }
            else
                enqueueSnackbar(response.error, { variant: "error" });
        },
        moreMenuItems: (() => {
            const menuItems = [];

            // Permitir crear si es administrador o tiene permisos de supervisión de usuarios
            if (usuario.nivel_usuario === 1 || usuario.sup_usuarios)
                menuItems.push({
                    label: "Crear nuevo usuario",
                    secondary: "Agregar una persona al sistema",
                    icon: <AddCircleOutlineRoundedIcon sx={{ color: green[600] }} />,
                    itemColor: green[600],
                    action: () => onNuevo(),
                });

            return menuItems;
        })(),
        rowActions: (row) => {
            const acciones = [];

            /*acciones.push({
                label: "Ver historial", icon: <HistoryRoundedIcon sx={{ color: blue[500] }} />, action: () => {
                    setIdReferenciaVerLogModal(row.id_usuario);
                    setReferenciaVerLogModal(Formatter.log_referencia.RECIB_DERIV.id);
                }
            });*/
            
            // Permitir editar si es administrador o tiene permisos de supervisión de usuarios
            if (usuario.nivel_usuario === 1 || usuario.sup_usuarios)
                acciones.push({
                    label: "Modificar datos",
                    secondary: "Cambiar nombre, email u otros datos de este usuario",
                    icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                    itemColor: blue[600],
                    action: () => onEditar(row),
                });
            
            // Permitir reestablecer contraseña si es administrador o tiene permisos de supervisión de usuarios
            if (usuario.nivel_usuario === 1 || usuario.sup_usuarios)
                acciones.push({
                    label: "Enviar enlace para cambiar contraseña",
                    secondary: "Se enviará un correo a este usuario con un enlace seguro",
                    icon: <LockResetRoundedIcon sx={{ color: orange[700] }} />,
                    itemColor: orange[700],
                    action: () => onReestablecerClave(row),
                });
            
            // Permitir eliminar si es administrador o tiene permisos de supervisión de usuarios
            if (usuario.nivel_usuario === 1 || usuario.sup_usuarios)
                acciones.push({
                    label: "Eliminar usuario",
                    secondary: "Se borrará de forma permanente. Esta acción no se puede deshacer.",
                    icon: <DeleteRoundedIcon sx={{ color: red[600] }} />,
                    itemColor: red[600],
                    dividerBefore: true,
                    action: () => onEliminar(row),
                });

            return acciones;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [usuario.id_usuario, usuario.token, usuario.nivel_usuario, usuario.sup_usuarios]);

    /*useEffect(() => {
        tableMain.loadData();
    }, [tableMain]);*/

    return (
        <Layout nombrePagina="Usuarios">
            <Fade in={true} timeout={900}>
                <Grid size={12}>
                        <MaterialTable
                            title={tableMain.title}
                            loadData={tableMain.loadData}
                            headers={tableMain.headers}
                            data={rowsTableMain}
                            moreMenuItems={tableMain.moreMenuItems}
                            selectable={false}
                            rowActions={tableMain.rowActions}
                            idName={tableMain.idName}
                            defaultOrder={tableMain.defaultOrder}
                            defaultRowsPerPage={10}
                            needUpdate={tableMainNeedUpdate}
                        />
                </Grid>
            </Fade>

            <VerLogDialog
                referencia={referenciaVerLogModal}
                setReferencia={setReferenciaVerLogModal}
                id_referencia={idReferenciaVerLogModal}
                id_usuario={usuario.id_usuario}
            />
        </Layout>
    );
}

export default Page;